import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../../state/index";
import { format } from "date-fns";
import { Pagination, Modal } from "antd";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ContactSupportIcon from "@mui/icons-material/ContactSupport";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import LawContactAPI from "../../utils/api/LawContactAPI";
import { TableSkeleton } from "../skeletons";
import "./law.css";

const AVATAR_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
];

const avatarColor = (str = "") => {
  let sum = 0;
  for (let i = 0; i < str.length; i++) sum += str.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

const initials = (first = "", last = "") =>
  ((first[0] || "") + (last[0] || "")).toUpperCase() || "?";

const capitalizeWords = (name) => {
  if (!name) return "";
  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const LawContactTable = () => {
  const dispatch = useDispatch();
  const { top_loader } = bindActionCreators(actionCreators, dispatch);

  const [recperpage, SetRecPerPage] = useState(25);
  const [activepage, SetActivePage] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageModal, setMessageModal] = useState({ visible: false, item: null });

  const sno = recperpage * (activepage - 1);

  const getAll = (params) => {
    setLoading(true);
    top_loader(true, 45);
    LawContactAPI.getAll(params)
      .then((result) => {
        if (result.data.status) {
          setData(result.data.data);
          setTotalData(result.data.total);
        } else {
          setData([]);
          setTotalData(0);
        }
        setLoading(false);
        top_loader(true, 100);
        top_loader(false, 0);
      })
      .catch(() => {
        setData([]);
        setLoading(false);
        top_loader(true, 100);
        top_loader(false, 0);
      });
  };

  useEffect(() => {
    getAll({ page: activepage, limit: recperpage });
    // eslint-disable-next-line
  }, [activepage, recperpage]);

  const handleRefresh = () => {
    SetActivePage(1);
    getAll({ page: 1, limit: recperpage });
  };

  const activeItem = messageModal.item;

  return (
    <Container fluid className="law-page custom-sidebar">
      <div className="law-shell">
        {/* Header */}
        <div className="law-header">
          <div className="law-header__left">
            <span className="law-header__icon">
              <ContactSupportIcon style={{ color: "#fff" }} />
            </span>
            <div>
              <h1 className="law-header__title">Law Contact Form</h1>
              <p className="law-header__subtitle">
                Enquiries submitted through the contact form
              </p>
            </div>
          </div>
          <div className="law-header__actions">
            <span className="law-count-pill">{totalData} Total</span>
            <button
              className="law-icon-btn"
              onClick={handleRefresh}
              title="Refresh"
            >
              <RefreshOutlinedIcon />
            </button>
          </div>
        </div>

        {/* Table card */}
        <div className="law-card">
          {loading ? (
            <div style={{ padding: "16px" }}>
              <TableSkeleton rows={10} columns={6} hasActions={false} />
            </div>
          ) : (
            <div className="law-table-wrap">
              <table className="law-table">
                <thead>
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th>Submitted</th>
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 &&
                    data.map((item, index) => (
                      <tr key={item._id || index}>
                        <td className="law-muted">{sno + index + 1}</td>
                        <td>
                          <div className="law-name-cell">
                            <span
                              className="law-avatar"
                              style={{
                                background: avatarColor(
                                  `${item.first_name}${item.last_name}`
                                ),
                              }}
                            >
                              {initials(item.first_name, item.last_name)}
                            </span>
                            <span className="law-name-primary">
                              {capitalizeWords(item.first_name)}{" "}
                              {capitalizeWords(item.last_name)}
                            </span>
                          </div>
                        </td>
                        <td>{item.email}</td>
                        <td>
                          <span className="law-chip law-chip--subject">
                            {item.subject}
                          </span>
                        </td>
                        <td>
                          {item.message ? (
                            <span
                              className="law-link"
                              onClick={() =>
                                setMessageModal({ visible: true, item })
                              }
                            >
                              <ChatBubbleOutlineIcon style={{ fontSize: "15px" }} />
                              View
                            </span>
                          ) : (
                            <span className="law-muted">—</span>
                          )}
                        </td>
                        <td className="law-muted">
                          {item.created_at
                            ? format(new Date(item.created_at), "dd MMM yyyy")
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className="law-empty">
                          <InboxOutlinedIcon style={{ fontSize: "44px", color: "#cbd5e1" }} />
                          <h3>No enquiries yet</h3>
                          <p>Contact form submissions will appear here.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="law-footer">
            <div className="law-rows-per-page">
              <span>Rows per page</span>
              <select
                onChange={(event) => {
                  SetRecPerPage(Number(event.target.value));
                  SetActivePage(1);
                }}
                value={recperpage}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
            <Pagination
              total={totalData}
              showSizeChanger={false}
              current={activepage}
              size="small"
              pageSize={recperpage}
              onChange={(page) => SetActivePage(page)}
            />
          </div>
        </div>
      </div>

      {/* Message modal */}
      <Modal
        title={null}
        open={messageModal.visible}
        onCancel={() => setMessageModal({ visible: false, item: null })}
        footer={null}
        width={540}
      >
        {activeItem && (
          <div style={{ paddingTop: "6px" }}>
            <div className="law-name-cell" style={{ marginBottom: "16px" }}>
              <span
                className="law-avatar"
                style={{
                  background: avatarColor(
                    `${activeItem.first_name}${activeItem.last_name}`
                  ),
                }}
              >
                {initials(activeItem.first_name, activeItem.last_name)}
              </span>
              <div>
                <div className="law-name-primary">
                  {capitalizeWords(activeItem.first_name)}{" "}
                  {capitalizeWords(activeItem.last_name)}
                </div>
                <div className="law-name-secondary">{activeItem.email}</div>
              </div>
            </div>
            <div className="law-field">
              <div className="law-field__label">Subject</div>
              <div className="law-field__value">{activeItem.subject}</div>
            </div>
            <div className="law-field__label" style={{ marginBottom: "6px" }}>
              Message
            </div>
            <div className="law-description">{activeItem.message}</div>
          </div>
        )}
      </Modal>
    </Container>
  );
};

export default LawContactTable;
